import {formatLocalInstant} from '../core/time.js';
import {button,escapeHtml,statusPill} from '../ui/components.js';
import {formatElapsedDuration} from './stage-timer.js';

function temperature(value){
  if(value==null||value==='')return '—';
  const number=Number(value);
  if(!Number.isFinite(number))return '—';
  return number.toFixed(Number.isInteger(number)?0:1)+'°C';
}

function targetText(target){
  if(!target)return 'Brak celu temperatury komory';
  const parts=[];
  if(target.targetChamberCelsius!=null)parts.push('cel '+temperature(target.targetChamberCelsius));
  if(target.minChamberCelsius!=null||target.maxChamberCelsius!=null){
    const min=target.minChamberCelsius==null?'—':temperature(target.minChamberCelsius);
    const max=target.maxChamberCelsius==null?'—':temperature(target.maxChamberCelsius);
    parts.push('zakres '+min+'–'+max);
  }
  return parts.join(' · ')||'Brak celu temperatury komory';
}

function timeText(model){
  const stage=model.timer.stage;
  if(stage.plannedMinutes==null)return 'Etap bez planowanego czasu';
  if(stage.overdue)return 'Po planie o '+formatElapsedDuration(stage.overdueMs);
  return 'Pozostało '+formatElapsedDuration(stage.remainingMs)+' z '+Math.round(stage.plannedMinutes)+' min';
}

function quickAction(action){
  return button({
    id:'live-quick-'+action.id,
    label:action.label,
    className:'live-quick-button'
  });
}

function targetSummary(target){
  if(target==null)return 'brak celu';
  if(typeof target==='number')return 'cel '+temperature(target);
  if(target.targetChamberCelsius!=null)return 'cel '+temperature(target.targetChamberCelsius);
  if(target.minChamberCelsius!=null||target.maxChamberCelsius!=null){
    return 'zakres '+temperature(target.minChamberCelsius)+'–'+temperature(target.maxChamberCelsius);
  }
  return 'brak celu';
}

function temperatureScopeOption({value,label,last,target,checked=false}){
  return '<label class="live-temperature-scope">'+
    '<input type="radio" name="temperatureScope" value="'+escapeHtml(value)+'"'+(checked?' checked':'')+'>'+
    '<span><strong>'+escapeHtml(label)+'</strong><small>Ostatnio '+escapeHtml(temperature(last))+
    ' · '+escapeHtml(targetSummary(target))+'</small></span></label>';
}

function temperatureActions(model){
  const chamber=temperatureScopeOption({
    value:'chamber',
    label:'Komora',
    last:model.latestChamber?.valueCelsius??null,
    target:model.currentStage?.target||null,
    checked:true
  });
  const items=(model.internalTemperatures||[]).map(row=>temperatureScopeOption({
    value:'item:'+row.itemId,
    label:row.name,
    last:row.latest?.valueCelsius??null,
    target:row.target
  })).join('');

  return '<section id="live-temperature-panel" class="panel live-temperature-panel" hidden>'+
    '<div class="panel-heading"><strong>Dodaj temperaturę</strong><span>Wybierz pomiar, wpisz wartość i zapisz.</span></div>'+
    '<form id="live-temperature-form" class="live-temperature-form" novalidate>'+
      '<div class="live-temperature-scopes">'+chamber+items+'</div>'+
      '<label class="field"><span class="field-label">Temperatura °C</span>'+
        '<input name="temperatureValue" type="text" inputmode="decimal" autocomplete="off" enterkeyhint="done" placeholder="np. 78,5" required>'+
      '</label>'+
      '<div id="live-temperature-error" class="field-error" role="alert" aria-live="polite"></div>'+
      '<div class="actions">'+button({label:'Zapisz temperaturę',type:'submit'})+'</div>'+
    '</form></section>';
}

function stageActions(model){
  if(model.stageControlBlocked){
    return '<section class="panel live-stage-actions-panel">'+
      '<div class="panel-heading"><strong>Sterowanie etapem wstrzymane</strong><span>Najpierw rozstrzygnij konflikt synchronizacji tej sesji.</span></div>'+
      '<p class="panel-copy">Temperatury, notatki, zdjęcia i niezależne zdarzenia Live nadal możesz zapisywać. Nowa zmiana etapu zostanie odblokowana po przyjęciu stanu serwera i rozstrzygnięciu odrzuconej lokalnej zmiany.</p>'+
      '</section>';
  }

  const previous=(model.stageChoices||[]).filter(stage=>stage.relation==='previous');
  const later=(model.stageChoices||[]).filter(stage=>stage.relation==='later');
  const next=model.nextStage
    ?button({
      id:'live-stage-next',
      label:'Zakończ etap → '+model.nextStage.name,
      className:'live-stage-next-button',
      attributes:{'data-live-stage-next':model.nextStage.id}
    })
    :'';

  const reopen=previous.map(stage=>button({
    label:'Wróć do: '+stage.name,
    variant:'secondary',
    className:'live-stage-choice',
    attributes:{'data-live-stage-reopen':stage.id}
  })).join('');

  const skip=later.map(stage=>button({
    label:'Pomiń do: '+stage.name,
    variant:'secondary',
    className:'live-stage-choice',
    attributes:{'data-live-stage-skip':stage.id}
  })).join('');

  return '<section class="panel live-stage-actions-panel">'+
    '<div class="panel-heading"><strong>Sterowanie etapem</strong><span>Każda zmiana zostanie zachowana w historii Live.</span></div>'+
    (next?'<div class="actions">'+next+'</div>':'<p class="panel-copy">To ostatni zaplanowany etap.</p>')+
    ((reopen||skip)?'<details><summary><strong>Inny etap</strong></summary><div class="live-stage-choice-grid">'+reopen+skip+'</div></details>':'')+
    '<details><summary><strong>Dodaj własny etap</strong></summary>'+
      '<form id="live-custom-stage-form" class="live-custom-stage-form">'+
        '<label class="field"><span class="field-label">Nazwa etapu</span>'+
          '<input name="customStageName" type="text" maxlength="160" autocomplete="off" placeholder="np. Dodatkowe dosuszanie" required>'+
        '</label>'+
        '<div class="actions">'+button({type:'submit',label:'Dodaj i rozpocznij etap'})+'</div>'+
      '</form>'+
    '</details>'+
    '</section>';
}

function itemOptions(model,{emptyLabel='Bez przypisania do produktu'}={}){
  return '<option value="">'+escapeHtml(emptyLabel)+'</option>'+
    (model.products||[]).map(item=>
      '<option value="'+escapeHtml(item.id)+'">'+escapeHtml(item.name)+'</option>'
    ).join('');
}

function noteActions(model){
  return '<section id="live-note-panel" class="panel live-capture-panel" hidden>'+
    '<div class="panel-heading"><strong>Dodaj notatkę</strong><span>Zapisze się od razu, także bez internetu.</span></div>'+
    '<form id="live-note-form" class="live-capture-form" novalidate>'+
      '<label class="field"><span class="field-label">Notatka</span>'+
        '<textarea name="liveNote" maxlength="4000" rows="3" placeholder="Co warto zapamiętać z tego momentu?" required></textarea>'+
      '</label>'+
      '<label class="field"><span class="field-label">Produkt (opcjonalnie)</span>'+
        '<select name="liveNoteItemId">'+itemOptions(model)+'</select>'+
      '</label>'+
      '<div id="live-note-error" class="field-error" role="alert" aria-live="polite"></div>'+
      '<div class="actions">'+button({type:'submit',label:'Zapisz notatkę'})+'</div>'+
    '</form></section>';
}

function photoActions(model){
  return '<section id="live-photo-panel" class="panel live-capture-panel" hidden>'+
    '<div class="panel-heading"><strong>Dodaj zdjęcie</strong><span>Plik zostanie zabezpieczony lokalnie przed próbą wysłania.</span></div>'+
    '<form id="live-photo-form" class="live-capture-form" novalidate>'+
      '<label class="field"><span class="field-label">Zdjęcie</span>'+
        '<input name="livePhoto" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" required>'+
      '</label>'+
      '<label class="field"><span class="field-label">Produkt (opcjonalnie)</span>'+
        '<select name="livePhotoItemId">'+itemOptions(model)+'</select>'+
      '</label>'+
      '<div id="live-photo-error" class="field-error" role="alert" aria-live="polite"></div>'+
      '<div class="actions">'+button({type:'submit',label:'Zapisz zdjęcie'})+'</div>'+
    '</form></section>';
}

function timelineTypeLabel(type){
  return ({
    stage_start:'Start etapu',
    stage_end:'Koniec etapu',
    temperature:'Temperatura',
    event:'Zdarzenie',
    note:'Notatka',
    photo:'Zdjęcie'
  })[String(type||'')]||'Live';
}

function timelineHistory(model){
  const timeline=model.timeline||{entries:[],total:0,visibleCount:0,hiddenCount:0,hasEarlier:false};
  const entries=timeline.entries||[];
  const list=entries.length
    ?'<div class="live-timeline-list">'+entries.map(row=>{
      const time=formatLocalInstant(row.timestamp,{
        locale:'pl-PL',
        options:{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}
      });
      const scope=row.meta?.itemName?' · '+row.meta.itemName:'';
      const pending=row.pending?'<span class="live-timeline-pending">lokalnie</span>':'';
      return '<article class="live-timeline-entry live-timeline-'+escapeHtml(row.type)+'" data-live-timeline-type="'+escapeHtml(row.type)+'">'+
        '<div class="live-timeline-marker" aria-hidden="true"></div>'+
        '<div class="live-timeline-content">'+
          '<div class="live-timeline-meta"><span>'+escapeHtml(time)+'</span><span>'+escapeHtml(timelineTypeLabel(row.type)+scope)+'</span>'+pending+'</div>'+
          '<strong>'+escapeHtml(row.label)+'</strong>'+
          (row.detail?'<p>'+escapeHtml(row.detail)+'</p>':'')+
        '</div>'+
      '</article>';
    }).join('')+'</div>'
    :'<p class="panel-copy">Historia Live pojawi się po pierwszym zapisie procesu.</p>';

  const more=timeline.hasEarlier
    ?'<div class="actions live-timeline-more">'+button({
      id:'live-timeline-more',
      label:'Pokaż wcześniejsze ('+timeline.hiddenCount+')',
      variant:'secondary'
    })+'</div>'
    :'';

  const subtitle=timeline.total
    ?'Pokazano '+timeline.visibleCount+' z '+timeline.total+' zapisów procesu.'
    :'Etapy, temperatury, zdarzenia, notatki i zdjęcia w jednej historii.';

  return '<section id="live-timeline" class="panel live-timeline">'+
    '<div class="panel-heading"><strong>Przebieg Live</strong><span>'+escapeHtml(subtitle)+'</span></div>'+
    more+list+
    '</section>';
}

function eventActions(model){
  const woods=(model.woodChoices||[]).map(wood=>button({
    label:'Dodano: '+wood.name,
    variant:'secondary',
    className:'live-event-action',
    attributes:{'data-live-event':'wood_added','data-live-wood-id':wood.id}
  })).join('');
  const products=(model.products||[]).map(item=>button({
    label:'Sprawdzono: '+item.name,
    variant:'secondary',
    className:'live-event-action',
    attributes:{'data-live-event':'product_checked','data-live-item-id':item.id}
  })).join('');

  return '<details class="panel live-event-panel"><summary><strong>Szybkie zdarzenia</strong></summary>'+
    '<div class="live-event-actions">'+
    button({label:'Otwarto komorę',variant:'secondary',className:'live-event-action',attributes:{'data-live-event':'chamber_opened'}})+
    woods+products+
    button({label:'Inne zdarzenie',variant:'secondary',className:'live-event-action',attributes:{'data-live-event':'custom'}})+
    '</div></details>';
}

function selected(value,current){return String(value)===String(current)?' selected':'';}
function checked(value){return value?' checked':'';}

function notificationSettings(preferences={}){
  const prefs={
    enabled:false,
    stageEndEnabled:true,
    stageEndLeadMinutes:5,
    temperatureIntervalMinutes:null,
    ...(preferences||{})
  };
  const interval=prefs.temperatureIntervalMinutes??'';
  return '<details class="panel live-notification-settings"><summary><strong>Przypomnienia Live</strong></summary>'+
    '<form id="live-notification-form" class="smoker-form" novalidate>'+
      '<label class="live-temperature-scope"><input name="liveNotificationsEnabled" type="checkbox"'+checked(prefs.enabled)+'>'+
        '<span><strong>Włącz przypomnienia na tym urządzeniu</strong><small>System poprosi o zgodę dopiero po włączeniu tej opcji.</small></span></label>'+
      '<label class="live-temperature-scope"><input name="stageEndEnabled" type="checkbox"'+checked(prefs.stageEndEnabled)+'>'+
        '<span><strong>Koniec etapu</strong><small>Przypomnij przed zaplanowanym końcem aktywnego etapu.</small></span></label>'+
      '<label class="field"><span class="field-label">Ile minut wcześniej</span><select name="stageEndLeadMinutes">'+
        [0,5,10,15,30].map(value=>'<option value="'+value+'"'+selected(value,prefs.stageEndLeadMinutes)+'>'+value+'</option>').join('')+
      '</select></label>'+
      '<label class="field"><span class="field-label">Przypomnienie o temperaturze</span><select name="temperatureIntervalMinutes">'+
        '<option value=""'+selected('',interval)+'>Wyłączone</option>'+
        [15,30,60].map(value=>'<option value="'+value+'"'+selected(value,interval)+'>Co '+value+' min</option>').join('')+
      '</select></label>'+
      '<p class="panel-copy">Ustawienia są lokalne dla tego urządzenia. Treść powiadomień nie zawiera nazw produktów ani danych sesji.</p>'+
      '<div class="actions">'+button({type:'submit',label:'Zapisz przypomnienia',variant:'secondary'})+'</div>'+
    '</form></details>';
}

export function liveDashboardMarkup(model={},options={}){
  const internal=(model.internalTemperatures||[]).map(row=>
    '<article class="live-temp-row"><div><strong>'+escapeHtml(row.name)+'</strong>'+
    '<span>Cel '+escapeHtml(temperature(row.target))+'</span></div>'+
    '<strong class="live-temp-value">'+escapeHtml(temperature(row.latest?.valueCelsius))+'</strong></article>'
  ).join('')||'<p class="panel-copy">Brak produktów z pomiarem temperatury wewnętrznej.</p>';

  const next=model.nextStage
    ?'<div class="live-next-stage"><span>Następny etap</span><strong>'+escapeHtml(model.nextStage.name)+'</strong></div>'
    :'<div class="live-next-stage"><span>Następny etap</span><strong>To ostatni etap planu</strong></div>';

  return '<section class="live-dashboard">'+
    '<div class="actions">'+button({id:'journal-back',label:'← Wszystkie wędzenia',variant:'ghost'})+'</div>'+
    '<header class="live-hero">'+
      '<div><span class="eyebrow">Wędzenie trwa</span><h2>'+escapeHtml(model.sessionTitle)+'</h2>'+
      '<p>'+(model.products||[]).map(item=>escapeHtml(item.name)).join(' · ')+'</p></div>'+
      statusPill(['LIVE'])+
    '</header>'+
    '<section class="live-stage panel">'+
      '<div class="live-stage-heading"><div><span>Aktualny etap</span><h3>'+escapeHtml(model.currentStage?.name||'—')+'</h3></div>'+
      '<strong class="live-stage-clock" data-live-stage-elapsed>'+escapeHtml(formatElapsedDuration(model.timer?.stage?.elapsedMs))+'</strong></div>'+
      '<p>'+escapeHtml(targetText(model.currentStage?.target))+'</p>'+
      '<p data-live-stage-remaining>'+escapeHtml(timeText(model))+'</p>'+
      '<p data-live-clock-warning class="panel-copy" role="status" hidden>Wykryto istotny skok zegara urządzenia. Licznik Live zachowuje ciągłość, a zapisane znaczniki czasu nie są automatycznie zmieniane.</p>'+
    '</section>'+
    '<section class="live-metrics">'+
      '<article class="panel live-metric"><span>Czas sesji</span><strong data-live-session-elapsed>'+escapeHtml(formatElapsedDuration(model.timer?.session?.elapsedMs))+'</strong></article>'+
      '<article class="panel live-metric"><span>Komora</span><strong>'+escapeHtml(temperature(model.latestChamber?.valueCelsius))+'</strong>'+
        '<small>'+(model.latestChamber?'Ostatni pomiar '+escapeHtml(formatLocalInstant(model.latestChamber.recordedAt,{locale:'pl-PL',options:{hour:'2-digit',minute:'2-digit'}})):'Brak pomiaru')+'</small></article>'+
    '</section>'+
    '<section class="panel live-internal"><div class="panel-heading"><strong>Temperatura produktów</strong><span>Ostatni pomiar i cel aktywnego etapu</span></div>'+internal+'</section>'+
    next+
    stageActions(model)+
    notificationSettings(options?.notificationPreferences||null)+
    '<section class="live-quick-actions" aria-label="Szybkie akcje">'+(model.quickActions||[]).map(quickAction).join('')+'</section>'+
    temperatureActions(model)+
    noteActions(model)+
    photoActions(model)+
    eventActions(model)+
    timelineHistory(model)+
    '<section class="panel live-finish-panel"><div class="panel-heading"><strong>Zakończ wędzenie</strong><span>Zamknij aktywny etap i przejdź do oceny wyniku.</span></div>'+
      '<p class="panel-copy">Historia Live, temperatury, zdjęcia, notatki i dane oczekujące na synchronizację pozostaną zachowane.</p>'+
      '<div class="actions">'+button({id:'live-finish-session',label:'Zakończ wędzenie',variant:'secondary'})+'</div></section>'+
    '<p class="panel-copy live-plan-lock">Plan jest zablokowany po rozpoczęciu. Zmiany procesu zapisuj jako zdarzenia Live.</p>'+
    '</section>';
}
